import React, { useEffect } from 'react';
import { XMarkIcon, CreditCardIcon, CalendarIcon, ExclamationCircleIcon, ArrowPathIcon, CheckCircleIcon, StopCircleIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { useParcelasNfe, useErpPagamentoNfe } from '../../hooks/useXmlTerceiros';
import { money, formatDate } from '../../utils/format';

interface ParcelasDialogProps {
  nfeId: number | null;
  numeroNfe?: number;
  isOpen: boolean;
  onClose: () => void;
}

const ParcelasDialog: React.FC<ParcelasDialogProps> = ({
  nfeId,
  numeroNfe,
  isOpen,
  onClose,
}) => {
  const {
    data: parcelasData,
    isLoading,
    error,
  } = useParcelasNfe(nfeId ?? undefined, isOpen);

  const {
    data: erpData,
    isLoading: erpLoading,
    error: erpError,
  } = useErpPagamentoNfe(nfeId ?? undefined, isOpen);

  // Fechar com ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Bloquear scroll do body
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  if (!isOpen || !nfeId) return null;

  // Mescla XML × ERP por posição (após ordenação por vencimento já feita no backend)
  const xmlParcelas = parcelasData?.parcelas ?? [];
  const erpParcelas = erpData?.parcelas ?? [];
  const maxLen = Math.max(xmlParcelas.length, erpParcelas.length);
  const mergedRows = Array.from({ length: maxLen }, (_, i) => ({
    xml: xmlParcelas[i],
    erp: erpParcelas[i],
  }));

  const totalXml = xmlParcelas.reduce((sum, p) => sum + (p.valor || 0), 0);
  const totalErp = erpParcelas.reduce((sum, p) => sum + (p.valorParcela || 0), 0);
  const totalPendente = erpParcelas.reduce(
    (sum, p) => sum + (p.valorPendente || 0),
    0
  );

  const sameDate = (a?: string, b?: string) => {
    if (!a || !b) return false;
    return a.slice(0, 10) === b.slice(0, 10);
  };

  const sameValue = (a?: number, b?: number) => {
    if (a == null || b == null) return false;
    return Math.abs(a - b) < 0.01;
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-zinc-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Parcelamento da NF-e"
          className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-zinc-200">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-blue-500 p-2 rounded-lg flex-shrink-0">
                <CreditCardIcon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-zinc-900 truncate">
                  Parcelamento {numeroNfe ? `· NF-e ${numeroNfe}` : ''}
                </h2>
                <p className="text-xs text-zinc-600">
                  Duplicatas e forma de pagamento
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-100 rounded-lg transition-colors flex-shrink-0"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center py-10 text-zinc-500">
                <ArrowPathIcon className="h-6 w-6 animate-spin mr-2" />
                Carregando parcelamento...
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-100 rounded-lg text-sm text-rose-700">
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Não foi possível extrair as parcelas do XML desta NF-e.
                </span>
              </div>
            )}

            {!isLoading && !error && parcelasData && (
              <div className="space-y-5">
                {/* Fatura */}
                {parcelasData.fatura && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-zinc-50 rounded-lg">
                    {parcelasData.fatura.numero && (
                      <div>
                        <p className="text-xs text-zinc-500">Fatura</p>
                        <p className="font-semibold text-zinc-900">
                          {parcelasData.fatura.numero}
                        </p>
                      </div>
                    )}
                    {parcelasData.fatura.valorOriginal != null && (
                      <div>
                        <p className="text-xs text-zinc-500">Valor Original</p>
                        <p className="font-semibold text-zinc-900">
                          {money(parcelasData.fatura.valorOriginal)}
                        </p>
                      </div>
                    )}
                    {parcelasData.fatura.valorDesconto != null &&
                      parcelasData.fatura.valorDesconto > 0 && (
                        <div>
                          <p className="text-xs text-zinc-500">Desconto</p>
                          <p className="font-semibold text-rose-600">
                            - {money(parcelasData.fatura.valorDesconto)}
                          </p>
                        </div>
                      )}
                    {parcelasData.fatura.valorLiquido != null && (
                      <div>
                        <p className="text-xs text-zinc-500">Valor Líquido</p>
                        <p className="font-semibold text-emerald-700">
                          {money(parcelasData.fatura.valorLiquido)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Formas de pagamento */}
                {parcelasData.pagamentos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-700 mb-2">
                      Forma de Pagamento
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {parcelasData.pagamentos.map((pag, idx) => (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-lg text-sm"
                        >
                          <CreditCardIcon className="h-3.5 w-3.5 text-blue-600" />
                          <span className="text-zinc-700">
                            {pag.tipoDescricao}
                          </span>
                          {pag.valor > 0 && (
                            <span className="font-semibold text-zinc-900">
                              {money(pag.valor)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Banner de situação no ERP */}
            <div className="mt-5">
              {erpLoading && (
                <div className="flex items-center justify-center py-3 text-sm text-zinc-500 bg-zinc-50 rounded-lg">
                  <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" />
                  Consultando ERP...
                </div>
              )}

              {erpError && (
                <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-100 rounded-lg text-sm text-rose-700">
                  <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Não foi possível consultar o ERP.</span>
                </div>
              )}

              {!erpLoading && !erpError && erpData && (
                <div
                  className={`rounded-lg p-3 border ${
                    !erpData.faturada
                      ? 'bg-amber-50 border-amber-200'
                      : erpData.parcelasLiquidadas === erpData.totalParcelas
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {!erpData.faturada ? (
                      <ExclamationCircleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    ) : erpData.parcelasLiquidadas === erpData.totalParcelas ? (
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CircleStackIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-semibold ${
                          !erpData.faturada
                            ? 'text-amber-800'
                            : erpData.parcelasLiquidadas === erpData.totalParcelas
                            ? 'text-emerald-800'
                            : 'text-blue-800'
                        }`}
                      >
                        {!erpData.faturada
                          ? 'Não faturada no ERP'
                          : erpData.parcelasLiquidadas === erpData.totalParcelas
                          ? 'Totalmente liquidada no ERP'
                          : 'Faturada · liquidação parcial'}
                      </p>
                      {erpData.faturada && (
                        <p className="text-xs text-zinc-600 mt-0.5">
                          {erpData.parcelasLiquidadas}/{erpData.totalParcelas}{' '}
                          parcela(s) liquidada(s) · Total {money(erpData.valorTotal)}
                          {erpData.valorPendente > 0 && (
                            <>
                              {' '}
                              · Pendente{' '}
                              <span className="font-semibold text-rose-600">
                                {money(erpData.valorPendente)}
                              </span>
                            </>
                          )}
                        </p>
                      )}
                      {!erpData.faturada && erpData.mensagem && (
                        <p className="text-xs text-zinc-600 mt-0.5">
                          {erpData.mensagem}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Grid unificado: XML × ERP */}
            {mergedRows.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-zinc-700">
                    Parcelas · XML × ERP
                  </h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {mergedRows.length}{' '}
                    {mergedRows.length === 1 ? 'parcela' : 'parcelas'}
                  </span>
                </div>
                <div className="overflow-x-auto border border-zinc-200 rounded-lg">
                  <table className="min-w-full divide-y divide-zinc-200 text-sm">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th
                          rowSpan={2}
                          className="px-3 py-2 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider align-bottom"
                        >
                          #
                        </th>
                        <th
                          colSpan={2}
                          className="px-3 py-2 text-center text-xs font-semibold text-blue-700 uppercase tracking-wider border-l border-zinc-200"
                        >
                          XML (Duplicata)
                        </th>
                        <th
                          colSpan={3}
                          className="px-3 py-2 text-center text-xs font-semibold text-emerald-700 uppercase tracking-wider border-l border-zinc-200"
                        >
                          ERP (Contas a Pagar)
                        </th>
                        <th
                          rowSpan={2}
                          className="px-3 py-2 text-left text-xs font-semibold text-zinc-600 uppercase tracking-wider align-bottom border-l border-zinc-200"
                        >
                          Status
                        </th>
                      </tr>
                      <tr>
                        <th className="px-3 py-1.5 text-left text-[10px] font-medium text-zinc-500 uppercase tracking-wider border-l border-zinc-200">
                          <CalendarIcon className="h-3 w-3 inline mr-1" />
                          Vencimento
                        </th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-3 py-1.5 text-left text-[10px] font-medium text-zinc-500 uppercase tracking-wider border-l border-zinc-200">
                          Vencimento
                        </th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-3 py-1.5 text-right text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
                          Pendente
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-zinc-100">
                      {mergedRows.map((row, idx) => {
                        const { xml, erp } = row;
                        const vencMatch =
                          xml && erp && sameDate(xml.dataVencimento, erp.vencimento);
                        const valMatch =
                          xml && erp && sameValue(xml.valor, erp.valorParcela);
                        const isPago = erp?.liquidado;
                        return (
                          <tr
                            key={idx}
                            className={isPago ? 'bg-emerald-50/40' : 'hover:bg-zinc-50'}
                          >
                            <td className="px-3 py-2 font-medium text-zinc-900">
                              {erp?.numeroParcela ?? xml?.numero ?? idx + 1}
                            </td>
                            {/* XML */}
                            <td className="px-3 py-2 text-zinc-700 border-l border-zinc-100">
                              {xml?.dataVencimento ? formatDate(xml.dataVencimento) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-zinc-900">
                              {xml ? money(xml.valor) : '—'}
                            </td>
                            {/* ERP */}
                            <td className="px-3 py-2 text-zinc-700 border-l border-zinc-100">
                              <div className="flex items-center gap-1">
                                {erp?.vencimento ? formatDate(erp.vencimento) : '—'}
                                {erp && xml && !vencMatch && (
                                  <span
                                    title="Vencimento divergente do XML"
                                    className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-zinc-900">
                              <div className="flex items-center justify-end gap-1">
                                {erp ? money(erp.valorParcela) : '—'}
                                {erp && xml && !valMatch && (
                                  <span
                                    title="Valor divergente do XML"
                                    className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-700">
                              {erp && erp.valorPendente && erp.valorPendente > 0 ? (
                                <span className="text-rose-600 font-medium">
                                  {money(erp.valorPendente)}
                                </span>
                              ) : (
                                <span className="text-zinc-400">—</span>
                              )}
                            </td>
                            {/* Status */}
                            <td className="px-3 py-2 border-l border-zinc-100">
                              {!erp ? (
                                <span className="inline-flex items-center gap-1 text-zinc-400 text-xs">
                                  <ExclamationCircleIcon className="h-3.5 w-3.5" />
                                  Não faturada
                                </span>
                              ) : erp.liquidado ? (
                                <span
                                  className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"
                                  title={erp.tipoDescricao || ''}
                                >
                                  <CheckCircleIcon className="h-3.5 w-3.5" />
                                  {erp.ultimoPagamento
                                    ? formatDate(erp.ultimoPagamento)
                                    : 'Paga'}
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 text-zinc-500 text-xs"
                                  title={erp.tipoDescricao || ''}
                                >
                                  <StopCircleIcon className="h-3.5 w-3.5" />
                                  Em aberto
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-zinc-50 border-t-2 border-zinc-200">
                      <tr>
                        <td className="px-3 py-2 text-xs font-semibold text-zinc-600 uppercase">
                          Total
                        </td>
                        <td className="px-3 py-2 border-l border-zinc-100"></td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-blue-700">
                          {money(totalXml)}
                        </td>
                        <td className="px-3 py-2 border-l border-zinc-100"></td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-emerald-700">
                          {money(totalErp)}
                        </td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-rose-600">
                          {totalPendente > 0 ? money(totalPendente) : '—'}
                        </td>
                        <td className="px-3 py-2 border-l border-zinc-100"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Legenda */}
                {xmlParcelas.length > 0 && erpParcelas.length > 0 && (
                  <div className="mt-2 flex items-center gap-4 text-xs text-zinc-500">
                    <div className="flex items-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Divergência entre XML e ERP
                    </div>
                    {xmlParcelas.length !== erpParcelas.length && (
                      <div className="flex items-center gap-1 text-amber-700">
                        <ExclamationCircleIcon className="h-3.5 w-3.5" />
                        Quantidade de parcelas difere ({xmlParcelas.length} XML ×{' '}
                        {erpParcelas.length} ERP)
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Mensagem vazia */}
            {!isLoading && !error && parcelasData && mergedRows.length === 0 && (
              <div className="mt-5 text-sm text-zinc-500 bg-zinc-50 p-4 rounded-lg text-center">
                Esta NF-e não possui duplicatas — pagamento à vista ou sem
                parcelamento declarado.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-zinc-50 px-5 py-3 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParcelasDialog;
